import { Box } from '@unifyapps/ui/components/Box';
import { useSlot } from '@unifyapps/ui/slots';
import type { CreateSlotsAndSlotProps, SlotProps } from '@unifyapps/ui/slots/types';
import { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { useRegistryContext } from '../../context/RegistryProvider';
import { useModuleResources } from '../../hooks/fetchAndInvalidation/useModuleResources';
import { BlockDataAttributes } from '../../hooks/useBlockDataAttributes';
import { useGlobalAppStateStore } from '../../stores/app/GlobalAppStateStore';
import type { DataSourceRecordStoreState } from '../../stores/DataSourceRecordStore';
import { InterfaceModes, useInterfaceStore } from '../../stores/InterfaceStore';
import { getPageLayout } from '../page/InterfacePage';
import type { InterfacePageProvidersProps } from '../page/InterfacePageProviders';
import { InterfacePageProviders } from '../page/InterfacePageProviders';

type Props = Pick<
  InterfacePageProvidersProps,
  'emitPageEvent' | 'pageInputs' | 'onOutputChange'
> & {
  moduleId: string;
  moduleInterfaceRecordId?: string;
  onPageScopeKey: (id: string) => void;
  attributesAndEventListeners?: Record<string, unknown>;
  className?: string;
  style?: React.CSSProperties;
  setWrapperRef?: Dispatch<SetStateAction<HTMLDivElement | null>>;
} & CreateSlotsAndSlotProps<
    { loading?: React.ElementType },
    { loading: SlotProps<React.ElementType, object, object> }
  >;

interface ModuleWrapperProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  attributesAndEventListeners?: Record<string, unknown>;
  setWrapperRef?: Props['setWrapperRef'];
}

export function ModuleContent() {
  const layout = useInterfaceStore().hooks.page.useLayout();
  const { body } = getPageLayout(layout);
  const { registry } = useRegistryContext();
  const BlockRenderer = registry.getBlockRenderer();

  if (!body) return null;

  return <BlockRenderer blockId={body} parentBlockId={null} />;
}

export function PageScopeKeyCapture({ onPageScopeKey }: { onPageScopeKey: (id: string) => void }) {
  const pageScopeKey = useInterfaceStore().hooks.usePageScopeKey();

  useEffect(() => {
    if (pageScopeKey) {
      onPageScopeKey(pageScopeKey);
    }
  }, [pageScopeKey, onPageScopeKey]);

  return null;
}

export function ModuleWrapper({
  children,
  className,
  style,
  attributesAndEventListeners,
  setWrapperRef,
}: ModuleWrapperProps) {
  const pageScopeKey = useInterfaceStore().hooks.usePageScopeKey();
  const pageId = useInterfaceStore().hooks.useActiveInterfacePageId();

  const finalAttributesAndEventListeners = useMemo(() => {
    return {
      ...attributesAndEventListeners,
      [BlockDataAttributes.PageScopeKey]: pageScopeKey,
      [BlockDataAttributes.PageId]: pageId,
    };
  }, [attributesAndEventListeners, pageScopeKey, pageId]);

  return (
    <Box
      {...finalAttributesAndEventListeners}
      className={className}
      ref={setWrapperRef}
      style={style}
    >
      {children}
    </Box>
  );
}

export const Module = (props: Props) => {
  const {
    moduleId,
    moduleInterfaceRecordId,
    emitPageEvent,
    pageInputs,
    onOutputChange,
    attributesAndEventListeners,
    className,
    style,
    slots,
    slotProps,
    setWrapperRef,
    onPageScopeKey,
  } = props;

  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();

  const interfaceStore = useInterfaceStore();
  const pageMode = interfaceStore.hooks.useMode();

  const moduleMode = pageMode === InterfaceModes.BUILDER ? InterfaceModes.PREVIEW : pageMode;

  const {
    dataSources,
    error,
    interfacePage,
    isLoading,
    interfacePageNamespace,
    customBlocks,
    customBlocksSnapshots,
  } = useModuleResources({
    moduleId,
    moduleInterfaceRecordId,
  });

  const [Loading, loadingProps] = useSlot('loading', {
    elementType: (_props: Record<string, unknown>) => null,
    externalForwardedProps: { slots, slotProps },
    ownerState: {},
    className: '',
  });

  if (isLoading || !interfacePage || error) {
    return <Loading {...loadingProps} />;
  }

  return (
    <InterfacePageProviders
      customBlocks={customBlocks}
      customBlocksSnapshots={customBlocksSnapshots}
      dataSources={dataSources as unknown as DataSourceRecordStoreState['dataSources']}
      emitPageEvent={emitPageEvent}
      interfacePageNamespace={interfacePageNamespace}
      interfaceRecord={interfaceRecord}
      key={moduleId}
      mode={moduleMode}
      onOutputChange={onOutputChange}
      page={interfacePage}
      pageInputs={pageInputs}
    >
      <ModuleWrapper
        attributesAndEventListeners={attributesAndEventListeners}
        className={className}
        setWrapperRef={setWrapperRef}
        style={style}
      >
        <PageScopeKeyCapture onPageScopeKey={onPageScopeKey} />
        <ModuleContent />
      </ModuleWrapper>
    </InterfacePageProviders>
  );
};
